// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef } from 'react'
import type { FC } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { RefreshCw, Loader2 } from 'lucide-react'
import type { NodeAdminViewProps } from '../../../../core/types'
import { useServices } from '@/app-shell/services'
import { useAgents } from '@/subsystems/agents'

interface FederalConnectorSyncSettings {
  scorePipelineId: string
  maxBills: number
}

export const FederalConnectorSyncAdminView: FC<NodeAdminViewProps> = ({
  config,
  onChange,
  orgId,
  workspaceId,
}) => {
  const settings = config as FederalConnectorSyncSettings | undefined

  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [scorePipelineId, setScorePipelineId] = useState<string>(
    settings?.scorePipelineId ?? ''
  )
  const [maxBills, setMaxBills] = useState<number>(settings?.maxBills ?? 0)

  const apiClient = useServices().getClient()
  const { data: agents = [], isLoading } = useAgents(apiClient, orgId, workspaceId)

  useEffect(() => {
    onChangeRef.current?.('scorePipelineId', scorePipelineId)
  }, [scorePipelineId])

  useEffect(() => {
    onChangeRef.current?.('maxBills', maxBills)
  }, [maxBills])


  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Federal Connector Sync
        </CardTitle>
        <CardDescription>
          Trigger a score pipeline for each updated federal bill
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="score-pipeline-select">Score Pipeline</Label>
          <p className="text-xs text-muted-foreground">
            Pipeline to trigger for each updated bill
          </p>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading pipelines...
            </div>
          ) : agents.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No agents available in this workspace
            </p>
          ) : (
            <Select value={scorePipelineId} onValueChange={setScorePipelineId}>
              <SelectTrigger id="score-pipeline-select" className="w-full">
                <SelectValue placeholder="Select a pipeline..." />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.agentId} value={agent.agentId}>
                    {agent.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="max-bills-input">Max Bills</Label>
          <p className="text-xs text-muted-foreground">
            Maximum number of bills to process per sync. 0 means no limit.
          </p>
          <Input
            id="max-bills-input"
            type="number"
            min={0}
            value={maxBills}
            onChange={(e) => setMaxBills(Number(e.target.value))}
            className="w-32"
          />
        </div>
      </CardContent>
    </Card>
  )
}
